/**
 * LPA Completions API
 *
 * Auditor execution flow for LPA instances:
 * - start completion session
 * - save per-question responses
 * - complete / cancel session
 * - fetch detailed execution context + summary
 */

import { supabase } from '@/integrations/supabase/client';
import { fetchInstanceById } from './scheduleInstances';
import type {
  CompleteCompletionInput,
  LpaCompletion,
  LpaCompletionSummary,
  LpaCompletionWithDetails,
  LpaConformanceStatus,
  LpaResponse,
  LpaResponseAttachment,
  SaveResponseInput,
} from '../types';

function initConformanceCounts(): Record<LpaConformanceStatus, number> {
  return {
    conforming: 0,
    non_conforming: 0,
    not_applicable: 0,
    skipped: 0,
    question_disabled: 0,
  };
}

/**
 * Starts a completion for an instance.
 *
 * - Creates completion row
 * - Pre-creates `question_disabled` responses for inactive schedule questions
 * - Moves instance status to `in_progress`
 *
 * If an in-progress completion already exists for this instance, it is returned.
 */
export async function startCompletion(
  instanceId: string,
  tenantId?: string,
): Promise<LpaCompletion> {
  let instanceQuery = supabase
    .schema('core')
    .from('mod_lpa_schedule_instances')
    .select('*')
    .eq('id', instanceId);

  if (tenantId) {
    instanceQuery = instanceQuery.eq('tenant_id', tenantId);
  }

  const { data: instance, error: instanceError } = await instanceQuery.single();
  if (instanceError) throw instanceError;

  if (instance.status === 'completed') {
    throw new Error('This audit instance is already completed.');
  }
  if (instance.status === 'cancelled' || instance.status === 'skipped') {
    throw new Error('This audit instance cannot be started in its current status.');
  }

  let existingQuery = supabase
    .schema('core')
    .from('mod_lpa_completions')
    .select('*')
    .eq('schedule_instance_id', instanceId)
    .eq('status', 'in_progress')
    .order('started_at', { ascending: false })
    .limit(1);

  if (tenantId) {
    existingQuery = existingQuery.eq('tenant_id', tenantId);
  }

  const { data: existingInProgress, error: existingError } = await existingQuery.maybeSingle();
  if (existingError) throw existingError;
  if (existingInProgress) {
    return existingInProgress as unknown as LpaCompletion;
  }

  let completedQuery = supabase
    .schema('core')
    .from('mod_lpa_completions')
    .select('id')
    .eq('schedule_instance_id', instanceId)
    .eq('status', 'completed')
    .limit(1);

  if (tenantId) {
    completedQuery = completedQuery.eq('tenant_id', tenantId);
  }

  const { data: completedRow, error: completedError } = await completedQuery.maybeSingle();
  if (completedError) throw completedError;
  if (completedRow) {
    throw new Error('This audit instance has already been completed.');
  }

  const { data: completionData, error: completionError } = await supabase
    .schema('core')
    .from('mod_lpa_completions')
    .insert({
      tenant_id: instance.tenant_id,
      schedule_instance_id: instance.id,
      audit_id: instance.audit_id,
      auditor_id: instance.auditor_id,
      status: 'in_progress',
    })
    .select()
    .single();

  if (completionError) throw completionError;

  let questionQuery = supabase
    .schema('core')
    .from('mod_lpa_schedule_questions')
    .select('id, is_active')
    .eq('schedule_id', instance.schedule_id);

  if (tenantId) {
    questionQuery = questionQuery.eq('tenant_id', tenantId);
  }

  const { data: scheduleQuestions, error: questionError } = await questionQuery;
  if (questionError) throw questionError;

  const disabledResponses = (scheduleQuestions ?? [])
    .filter((q) => !q.is_active)
    .map((q) => ({
      tenant_id: instance.tenant_id,
      completion_id: completionData.id,
      schedule_question_id: q.id,
      conformance_status: 'question_disabled' as const,
      comment: null,
      duration_seconds: null,
    }));

  if (disabledResponses.length > 0) {
    const { error: disabledInsertError } = await supabase
      .schema('core')
      .from('mod_lpa_responses')
      .upsert(disabledResponses, {
        onConflict: 'completion_id,schedule_question_id',
        ignoreDuplicates: true,
      });

    if (disabledInsertError) throw disabledInsertError;
  }

  let updateInstanceQuery = supabase
    .schema('core')
    .from('mod_lpa_schedule_instances')
    .update({
      status: 'in_progress',
      completed_at: null,
    })
    .eq('id', instance.id);

  if (tenantId) {
    updateInstanceQuery = updateInstanceQuery.eq('tenant_id', tenantId);
  }

  const { error: updateInstanceError } = await updateInstanceQuery;
  if (updateInstanceError) throw updateInstanceError;

  return completionData as unknown as LpaCompletion;
}

/**
 * Upserts a response for one schedule question in a completion.
 */
export async function saveResponse(
  completionId: string,
  input: SaveResponseInput,
  tenantId?: string,
): Promise<LpaResponse> {
  let completionQuery = supabase
    .schema('core')
    .from('mod_lpa_completions')
    .select('id, tenant_id, schedule_instance_id, status')
    .eq('id', completionId);

  if (tenantId) {
    completionQuery = completionQuery.eq('tenant_id', tenantId);
  }

  const { data: completion, error: completionError } = await completionQuery.single();
  if (completionError) throw completionError;

  if (completion.status !== 'in_progress') {
    throw new Error('Only in-progress completions can be updated.');
  }

  const { data: instance, error: instanceError } = await supabase
    .schema('core')
    .from('mod_lpa_schedule_instances')
    .select('schedule_id')
    .eq('id', completion.schedule_instance_id)
    .single();
  if (instanceError) throw instanceError;

  let scheduleQuestionQuery = supabase
    .schema('core')
    .from('mod_lpa_schedule_questions')
    .select('id')
    .eq('id', input.schedule_question_id)
    .eq('schedule_id', instance.schedule_id);

  if (tenantId) {
    scheduleQuestionQuery = scheduleQuestionQuery.eq('tenant_id', tenantId);
  }

  const { data: scheduleQuestion, error: scheduleQuestionError } = await scheduleQuestionQuery.maybeSingle();
  if (scheduleQuestionError) throw scheduleQuestionError;
  if (!scheduleQuestion) {
    throw new Error('Question is not part of this completion schedule.');
  }

  const { data, error } = await supabase
    .schema('core')
    .from('mod_lpa_responses')
    .upsert(
      {
        tenant_id: completion.tenant_id,
        completion_id: completionId,
        schedule_question_id: input.schedule_question_id,
        conformance_status: input.conformance_status,
        comment: input.comment ?? null,
        duration_seconds: input.duration_seconds ?? null,
      },
      {
        onConflict: 'completion_id,schedule_question_id',
      },
    )
    .select()
    .single();

  if (error) throw error;
  return data as unknown as LpaResponse;
}

/**
 * Completes a completion and marks its instance as completed.
 */
export async function completeCompletion(
  completionId: string,
  input?: CompleteCompletionInput,
  tenantId?: string,
): Promise<LpaCompletion> {
  let completionQuery = supabase
    .schema('core')
    .from('mod_lpa_completions')
    .select('*')
    .eq('id', completionId);

  if (tenantId) {
    completionQuery = completionQuery.eq('tenant_id', tenantId);
  }

  const { data: completion, error: completionError } = await completionQuery.single();
  if (completionError) throw completionError;

  if (completion.status === 'completed') {
    return completion as unknown as LpaCompletion;
  }
  if (completion.status === 'cancelled') {
    throw new Error('Cancelled completion cannot be marked as completed.');
  }

  let responseQuery = supabase
    .schema('core')
    .from('mod_lpa_responses')
    .select('duration_seconds')
    .eq('completion_id', completionId);

  if (tenantId) {
    responseQuery = responseQuery.eq('tenant_id', tenantId);
  }

  const { data: responses, error: responseError } = await responseQuery;
  if (responseError) throw responseError;

  const summedDuration = (responses ?? []).reduce(
    (sum, r) => sum + Math.max(0, r.duration_seconds ?? 0),
    0,
  );
  const elapsedDuration = Math.max(
    0,
    Math.round((Date.now() - new Date(completion.started_at).getTime()) / 1000),
  );
  const durationSeconds = summedDuration > 0 ? summedDuration : elapsedDuration;
  const completedAt = new Date().toISOString();

  const completionUpdatePayload: Record<string, unknown> = {
    status: 'completed',
    completed_at: completedAt,
    duration_seconds: durationSeconds,
  };
  if (input?.notes !== undefined) {
    completionUpdatePayload.notes = input.notes;
  }

  let completionUpdateQuery = supabase
    .schema('core')
    .from('mod_lpa_completions')
    .update(completionUpdatePayload)
    .eq('id', completionId);

  if (tenantId) {
    completionUpdateQuery = completionUpdateQuery.eq('tenant_id', tenantId);
  }

  const { data: updatedCompletion, error: completionUpdateError } = await completionUpdateQuery
    .select()
    .single();
  if (completionUpdateError) throw completionUpdateError;

  let instanceUpdateQuery = supabase
    .schema('core')
    .from('mod_lpa_schedule_instances')
    .update({
      status: 'completed',
      completed_at: completedAt,
    })
    .eq('id', completion.schedule_instance_id);

  if (tenantId) {
    instanceUpdateQuery = instanceUpdateQuery.eq('tenant_id', tenantId);
  }

  const { error: instanceUpdateError } = await instanceUpdateQuery;
  if (instanceUpdateError) throw instanceUpdateError;

  return updatedCompletion as unknown as LpaCompletion;
}

/**
 * Cancels a completion and returns the instance to pending.
 */
export async function cancelCompletion(
  completionId: string,
  tenantId?: string,
): Promise<LpaCompletion> {
  let completionQuery = supabase
    .schema('core')
    .from('mod_lpa_completions')
    .select('*')
    .eq('id', completionId);

  if (tenantId) {
    completionQuery = completionQuery.eq('tenant_id', tenantId);
  }

  const { data: completion, error: completionError } = await completionQuery.single();
  if (completionError) throw completionError;

  if (completion.status === 'completed') {
    throw new Error('Completed completion cannot be cancelled.');
  }
  if (completion.status === 'cancelled') {
    return completion as unknown as LpaCompletion;
  }

  const completedAt = new Date().toISOString();
  const durationSeconds = Math.max(
    0,
    Math.round((Date.now() - new Date(completion.started_at).getTime()) / 1000),
  );

  let completionUpdateQuery = supabase
    .schema('core')
    .from('mod_lpa_completions')
    .update({
      status: 'cancelled',
      completed_at: completedAt,
      duration_seconds: durationSeconds,
    })
    .eq('id', completionId);

  if (tenantId) {
    completionUpdateQuery = completionUpdateQuery.eq('tenant_id', tenantId);
  }

  const { data: updatedCompletion, error: completionUpdateError } = await completionUpdateQuery
    .select()
    .single();
  if (completionUpdateError) throw completionUpdateError;

  let instanceUpdateQuery = supabase
    .schema('core')
    .from('mod_lpa_schedule_instances')
    .update({
      status: 'pending',
      completed_at: null,
    })
    .eq('id', completion.schedule_instance_id);

  if (tenantId) {
    instanceUpdateQuery = instanceUpdateQuery.eq('tenant_id', tenantId);
  }

  const { error: instanceUpdateError } = await instanceUpdateQuery;
  if (instanceUpdateError) throw instanceUpdateError;

  return updatedCompletion as unknown as LpaCompletion;
}

/**
 * Fetches one completion with resolved instance context and merged responses.
 *
 * Response rows are merged with schedule questions so unanswered active
 * questions are returned as placeholders (`is_placeholder=true`).
 */
export async function fetchCompletionById(
  completionId: string,
  tenantId?: string,
): Promise<LpaCompletionWithDetails | null> {
  let completionQuery = supabase
    .schema('core')
    .from('mod_lpa_completions')
    .select('*')
    .eq('id', completionId);

  if (tenantId) {
    completionQuery = completionQuery.eq('tenant_id', tenantId);
  }

  const { data: completionData, error: completionError } = await completionQuery.maybeSingle();
  if (completionError) throw completionError;
  if (!completionData) return null;

  const completion = completionData as unknown as LpaCompletion;

  const instance = await fetchInstanceById(
    completion.schedule_instance_id,
    tenantId ?? completion.tenant_id,
  );
  if (!instance) {
    throw new Error('Completion instance not found.');
  }

  let responseQuery = supabase
    .schema('core')
    .from('mod_lpa_responses')
    .select('*')
    .eq('completion_id', completionId);

  if (tenantId) {
    responseQuery = responseQuery.eq('tenant_id', tenantId);
  }

  const { data: responseData, error: responseError } = await responseQuery;
  if (responseError) throw responseError;

  const responses = (responseData ?? []) as unknown as LpaResponse[];
  const responseIds = responses.map((r) => r.id);

  const attachmentMap = new Map<string, LpaResponseAttachment[]>();
  if (responseIds.length > 0) {
    let attachmentQuery = supabase
      .schema('core')
      .from('mod_lpa_response_attachments')
      .select('*')
      .in('response_id', responseIds)
      .order('created_at', { ascending: true });

    if (tenantId) {
      attachmentQuery = attachmentQuery.eq('tenant_id', tenantId);
    }

    const { data: attachments, error: attachmentError } = await attachmentQuery;
    if (attachmentError) throw attachmentError;

    for (const attachment of (attachments ?? []) as unknown as LpaResponseAttachment[]) {
      const list = attachmentMap.get(attachment.response_id) ?? [];
      list.push(attachment);
      attachmentMap.set(attachment.response_id, list);
    }
  }

  const responseByScheduleQuestion = new Map(
    responses.map((r) => [r.schedule_question_id, r]),
  );

  const executionQuestions = instance.questions ?? [];

  const mergedResponses = executionQuestions.map((q) => {
    const response = responseByScheduleQuestion.get(q.schedule_question_id);
    const attachments = response ? (attachmentMap.get(response.id) ?? []) : [];

    return {
      id: response?.id ?? null,
      tenant_id: response?.tenant_id ?? completion.tenant_id,
      completion_id: completion.id,
      schedule_question_id: q.schedule_question_id,
      conformance_status: response?.conformance_status ?? null,
      comment: response?.comment ?? null,
      duration_seconds: response?.duration_seconds ?? null,
      created_at: response?.created_at ?? null,
      updated_at: response?.updated_at ?? null,
      is_placeholder: !response,
      sort_order: q.sort_order,
      is_question_active: q.is_active,
      question: q.question,
      attachments,
    };
  });

  return {
    ...completion,
    instance,
    responses: mergedResponses,
  };
}

/**
 * Returns aggregate counters for one completion.
 */
export async function fetchCompletionSummary(
  completionId: string,
  tenantId?: string,
): Promise<LpaCompletionSummary | null> {
  const completion = await fetchCompletionById(completionId, tenantId);
  if (!completion) return null;

  const counts = initConformanceCounts();

  for (const response of completion.responses) {
    if (response.conformance_status) {
      counts[response.conformance_status] += 1;
    }
  }

  const totalVisibleQuestions = completion.responses.filter((r) => r.is_question_active).length;
  const answeredCount = completion.responses.filter(
    (r) =>
      r.is_question_active &&
      r.conformance_status !== null &&
      r.conformance_status !== 'question_disabled',
  ).length;

  return {
    completion_id: completion.id,
    status: completion.status,
    started_at: completion.started_at,
    completed_at: completion.completed_at,
    duration_seconds: completion.duration_seconds,
    counts,
    answered_count: answeredCount,
    total_visible_questions: totalVisibleQuestions,
  };
}
